import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { BarcodeFormat } from '@zxing/library'

import { useNotification } from 'app/providers'

import { ALERT_CONSTANTS } from 'common/constants/alertConstants'

import { type DetectedBarcode, ZxingBarcodeScanner } from '../barcode-scanner'
import { scannerLog } from '../barcode-scanner/scanner-log'
import styles from './product-upc-scanner.module.scss'
import { getScannedUpcValue } from './upc-scanner.utils'

const PRODUCT_BARCODE_FORMATS = [
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
] as const

type CameraFacingMode = 'user' | 'environment'

const DEFAULT_FACING_MODE: CameraFacingMode = 'environment'

type ProductUpcScannerProps = {
  onScan: (upc: string) => void
}

export const ProductUpcScanner = ({ onScan }: ProductUpcScannerProps) => {
  const { setNotification } = useNotification()
  const onScanRef = useRef(onScan)
  const [facingMode, setFacingMode] = useState<CameraFacingMode>(DEFAULT_FACING_MODE)

  useEffect(() => {
    onScanRef.current = onScan
  }, [onScan])

  const scannerConstraints = useMemo<MediaTrackConstraints>(
    () => ({
      facingMode: { exact: facingMode },
      width: { ideal: 1920 },
      height: { ideal: 1080 },
      frameRate: { ideal: 30, max: 30 },
    }),
    [facingMode],
  )

  const handleFlipCamera = useCallback(() => {
    setFacingMode((current) => (current === 'user' ? 'environment' : 'user'))
  }, [])

  const handleScanSuccess = useCallback((result: DetectedBarcode[]) => {
    scannerLog('ProductUpcScanner.handleScanSuccess result =', result)
    const scannedUpc = getScannedUpcValue(result)
    scannerLog('ProductUpcScanner.handleScanSuccess scannedUpc =', scannedUpc)

    if (scannedUpc) {
      scannerLog('ProductUpcScanner: forwarding to onScan prop')
      onScanRef.current(scannedUpc)
    } else {
      scannerLog('ProductUpcScanner: getScannedUpcValue returned null; NOT forwarding')
    }
  }, [])

  const handleScanError = useCallback(
    (error: unknown) => {
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        title: 'Scan error',
        description: error instanceof Error ? error.message : 'Something went wrong',
      })
    },
    [setNotification],
  )

  return (
    <div className={styles.upcScannerViewport}>
      <ZxingBarcodeScanner
        formats={PRODUCT_BARCODE_FORMATS}
        onScan={handleScanSuccess}
        onError={handleScanError}
        scanDelay={500}
        constraints={scannerConstraints}
        mirror={facingMode === 'user'}
      />
      <button
        type='button'
        className={styles.flipCameraButton}
        onClick={handleFlipCamera}
        aria-label={facingMode === 'user' ? 'Switch to back camera' : 'Switch to front camera'}
        title={facingMode === 'user' ? 'Switch to back camera' : 'Switch to front camera'}>
        <Icon icon='mdi:camera-flip-outline' width={22} height={22} />
      </button>
    </div>
  )
}
